import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { format, subDays } from 'date-fns';
import { TrendingUp, TrendingDown, DollarSign, BedDouble, Download, Wallet, PiggyBank, Car, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const COLORS = ['#1e3a8a', '#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'];

const ReportsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { hotel, rooms, roomTypes } = useHotel();
  const [occupancyData, setOccupancyData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd')
  });

  const currency = hotel?.currency || 'EUR';

  const fetchReports = useCallback(async (showRefreshing = false) => {
    if (!user?.hotel_id) return;
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const [occRes, revRes, expRes] = await Promise.all([
        axios.get(`${API}/hotels/${user.hotel_id}/reports/occupancy?from_date=${dateRange.from}&to_date=${dateRange.to}`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API}/hotels/${user.hotel_id}/reports/revenue?from_date=${dateRange.from}&to_date=${dateRange.to}`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API}/hotels/${user.hotel_id}/expenses/summary/by-category?from_date=${dateRange.from}&to_date=${dateRange.to}`, {
          headers: getAuthHeaders()
        })
      ]);
      setOccupancyData(occRes.data);
      setRevenueData(revRes.data);
      setExpenseSummary(expRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.hotel_id, dateRange, getAuthHeaders]);

  // Initial fetch and when date range changes
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Auto-refresh
  useEffect(() => {
    const intervalId = setInterval(() => fetchReports(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchReports]);

  const handleManualRefresh = () => fetchReports(true);

  const exportOccupancyPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/hotels/${user.hotel_id}/reports/occupancy/pdf?from_date=${dateRange.from}&to_date=${dateRange.to}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `occupancy_report_${dateRange.from}_${dateRange.to}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Occupancy PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportRevenuePDF = async () => {
    try {
      const response = await axios.get(
        `${API}/hotels/${user.hotel_id}/reports/revenue/pdf?from_date=${dateRange.from}&to_date=${dateRange.to}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `revenue_report_${dateRange.from}_${dateRange.to}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Revenue PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const exportToExcel = () => {
    if (!occupancyData?.daily_data) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Occupied Rooms', 'Available Rooms', 'Occupancy Rate (%)'];
    const rows = occupancyData.daily_data.map(d => [
      d.date,
      d.occupied,
      d.available,
      d.occupancy_rate
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `occupancy_report_${dateRange.from}_${dateRange.to}.csv`;
    link.click();
    
    toast.success('Report exported to CSV');
  };

  // Calculate totals
  const totalRoomRevenue = revenueData?.revenue?.paid || 0;
  const totalTransferIncome = revenueData?.transfers?.total || 0;
  const totalRevenue = revenueData?.combined_revenue || (totalRoomRevenue + totalTransferIncome);
  // Handle expense summary - it's an array of { category, total, count }
  const totalExpenses = Array.isArray(expenseSummary) 
    ? expenseSummary.reduce((sum, e) => sum + (e.total || 0), 0)
    : (expenseSummary?.total || 0);
  const netProfit = totalRevenue - totalExpenses;
  const isProfit = netProfit >= 0;

  // Room type distribution for pie chart
  const roomTypeData = roomTypes.map(rt => {
    const count = rooms.filter(r => r.room_type_id === rt.id).length;
    return { name: rt.name, value: count };
  }).filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:p-4" data-testid="reports-page">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Reports & Analytics</h1>
          <p className="text-muted-foreground">
            Performance metrics and financial insights
            {lastUpdated && <span className="ml-2 text-xs">• Updated: {format(lastUpdated, 'HH:mm:ss')}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" onClick={exportToExcel} data-testid="export-excel-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={exportOccupancyPDF} data-testid="export-occupancy-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Occupancy PDF
          </Button>
          <Button variant="outline" onClick={exportRevenuePDF} data-testid="export-revenue-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Revenue PDF
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                data-testid="report-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                data-testid="report-to-date"
              />
            </div>
            <Button onClick={fetchReports} data-testid="apply-date-range-btn">
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary - Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Occupancy</p>
                <p className="text-3xl font-bold">{occupancyData?.average_occupancy || 0}%</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950">
                <BedDouble className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Room Revenue</p>
                <p className="text-3xl font-bold text-emerald-600">{currency}{totalRoomRevenue.toFixed(0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Transfer Income</p>
                <p className="text-3xl font-bold text-cyan-600">{currency}{totalTransferIncome.toFixed(0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-cyan-50 dark:bg-cyan-950">
                <Car className="h-6 w-6 text-cyan-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{revenueData?.transfers?.count || 0} transfers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-3xl font-bold text-red-600">{currency}{totalExpenses.toFixed(0)}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950">
                <Wallet className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={isProfit ? 'border-emerald-200 dark:border-emerald-800' : 'border-red-200 dark:border-red-800'}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-3xl font-bold ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                  {isProfit ? '+' : ''}{currency}{netProfit.toFixed(0)}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${isProfit ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-red-50 dark:bg-red-950'}`}>
                {isProfit ? (
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">RevPAR</p>
                <p className="text-3xl font-bold">{currency}{revenueData?.metrics?.revpar?.toFixed(0) || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950">
                <PiggyBank className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Revenue per Available Room</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="financial" className="space-y-4">
        <TabsList className="print:hidden">
          <TabsTrigger value="financial">Financial Summary</TabsTrigger>
          <TabsTrigger value="occupancy">Occupancy</TabsTrigger>
          <TabsTrigger value="revenue">Revenue Details</TabsTrigger>
          <TabsTrigger value="distribution">Room Distribution</TabsTrigger>
        </TabsList>

        {/* Financial Summary Tab */}
        <TabsContent value="financial">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-emerald-600">Revenue Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                    <span className="text-emerald-700 dark:text-emerald-400">Room Revenue (from Check-ins)</span>
                    <span className="font-bold text-lg text-emerald-700 dark:text-emerald-400">
                      {currency}{totalRoomRevenue.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Car className="h-4 w-4 text-cyan-600" />
                      <span className="text-cyan-700 dark:text-cyan-400">Transfer Income</span>
                    </div>
                    <span className="font-bold text-lg text-cyan-700 dark:text-cyan-400">
                      {currency}{totalTransferIncome.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-emerald-100 dark:bg-emerald-900 rounded-lg border-2 border-emerald-300 dark:border-emerald-700">
                    <span className="font-semibold text-emerald-800 dark:text-emerald-300">Total Revenue</span>
                    <span className="font-bold text-xl text-emerald-800 dark:text-emerald-300">
                      {currency}{totalRevenue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Expense Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                    <span className="text-red-700 dark:text-red-400">Total Expenses</span>
                    <span className="font-bold text-lg text-red-700 dark:text-red-400">
                      {currency}{totalExpenses.toFixed(2)}
                    </span>
                  </div>
                  {expenseSummary?.by_category && Object.entries(expenseSummary.by_category).slice(0, 5).map(([category, amount]) => (
                    <div key={category} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                      <span className="capitalize">{category.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{currency}{amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Profit/Loss Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-6 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Room Revenue</p>
                    <p className="text-2xl font-bold text-emerald-600">{currency}{totalRoomRevenue.toFixed(2)}</p>
                  </div>
                  <div className="text-center p-6 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Transfer Income</p>
                    <p className="text-2xl font-bold text-cyan-600">{currency}{totalTransferIncome.toFixed(2)}</p>
                  </div>
                  <div className="text-center p-6 bg-red-50 dark:bg-red-950 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-600">{currency}{totalExpenses.toFixed(2)}</p>
                  </div>
                  <div className={`text-center p-6 rounded-lg ${isProfit ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-red-100 dark:bg-red-900'}`}>
                    <p className="text-sm text-muted-foreground mb-2">{isProfit ? 'Net Profit' : 'Net Loss'}</p>
                    <p className={`text-2xl font-bold ${isProfit ? 'text-emerald-700' : 'text-red-700'}`}>
                      {isProfit ? '+' : ''}{currency}{netProfit.toFixed(2)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Occupancy Tab */}
        <TabsContent value="occupancy">
          <Card>
            <CardHeader>
              <CardTitle>Daily Occupancy Rate</CardTitle>
            </CardHeader>
            <CardContent>
              {occupancyData?.daily_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={occupancyData.daily_data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => value.slice(5)}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      domain={[0, 100]}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, 'Occupancy']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="occupancy_rate" 
                      stroke="#1e3a8a" 
                      strokeWidth={2}
                      dot={{ fill: '#1e3a8a' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-16">No occupancy data for selected period.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                    <span className="text-emerald-700 dark:text-emerald-400">Room Revenue (from Check-ins)</span>
                    <span className="font-bold text-lg text-emerald-700 dark:text-emerald-400">
                      {currency}{totalRoomRevenue.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
                    <span className="text-cyan-700 dark:text-cyan-400">Transfer Income</span>
                    <span className="font-bold text-lg text-cyan-700 dark:text-cyan-400">
                      {currency}{totalTransferIncome.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-emerald-100 dark:bg-emerald-900 rounded-lg border-2 border-emerald-300 dark:border-emerald-700">
                    <span className="font-semibold text-emerald-800 dark:text-emerald-300">Total Revenue</span>
                    <span className="font-bold text-xl text-emerald-800 dark:text-emerald-300">
                      {currency}{totalRevenue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Key Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
                    <span>Total Reservations</span>
                    <span className="font-bold text-lg">{revenueData?.reservation_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
                    <span>Total Room Nights</span>
                    <span className="font-bold text-lg">{revenueData?.metrics?.total_room_nights || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
                    <span>Invoices Generated</span>
                    <span className="font-bold text-lg">{revenueData?.invoice_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <span className="text-blue-700 dark:text-blue-400">ADR (Avg Daily Rate)</span>
                    <span className="font-bold text-lg text-blue-700 dark:text-blue-400">
                      {currency}{revenueData?.metrics?.adr?.toFixed(2) || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Distribution Tab */}
        <TabsContent value="distribution">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Rooms by Type</CardTitle>
              </CardHeader>
              <CardContent>
                {roomTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={roomTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {roomTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted-foreground py-16">No room data available.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Room Type Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {roomTypes.map((rt, index) => {
                    const count = rooms.filter(r => r.room_type_id === rt.id).length;
                    return (
                      <div key={rt.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span>{rt.name}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{count} rooms</p>
                          <p className="text-sm text-muted-foreground">€{rt.base_price}/night</p>
                        </div>
                      </div>
                    );
                  })}
                  {roomTypes.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No room types configured.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
